import {
  faBuilding,
  faFileInvoiceDollar,
  faLink,
  faRectanglePro,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { EIntegrationTypes, Role } from '@feathr/blackbox';
import { NavItem } from '@feathr/components';
import { useAccount, useFlags, useLocalUrl, useUser } from '@feathr/extender/state';

import SecondaryNav from '../SecondaryNav';
import TertiaryNav from '../TertiaryNav';

interface ILocationState {
  pathname: string;
}

function SettingsNav(): JSX.Element {
  const localUrl = useLocalUrl();
  const { pathname } = useLocation<ILocationState>();
  const user = useUser();
  const account = useAccount();

  const { t } = useTranslation();

  const isFeathrAdmin = user.isSudoer;
  const isAdmin =
    isFeathrAdmin || account.get('acl').find((i) => i.user_id === user.id)?.role === Role.admin;
  const { activePackage, isPlatformAdvanced, isPro } = account;
  const showAPIKeys = account.isFalcon;
  const flags = useFlags();
  const showImis = isPlatformAdvanced || flags.hasImis;
  const showBlackbaudRaisersEdge = flags.hasBlackbaudRaisersEdge;
  const showGoogleAds = flags.hasGoogleAds;

  const userPathSuffix = ['user/profile', 'reset-password', 'notifications'];
  const accountPathSuffix = [
    'account/profile',
    'advertisers',
    'allow-list',
    'conversions',
    'domains',
    'emails',
    'fonts',
    'ip-filtering',
    'subscriptions',
    'users',
  ];

  const billingTertiaries = ['billing', 'invoices', 'license'];
  const onUserSetting = userPathSuffix.some((tert) => pathname.includes(tert));
  const onAccountSetting = accountPathSuffix.some((tert) => pathname.includes(tert));
  const onIntegrationSetting = pathname.includes('/settings/integrations');
  const onBillingSetting = billingTertiaries.some((tert) => pathname.includes(tert));

  // If the account has iMIS configured then it can't have Blackbaud Raiser's Edge NXT.
  const hasNoConflictsWithBlackbaud = account.conflictingIntegrations.includes(
    EIntegrationTypes.Imis,
  );

  // If the account has Blackbaud Raiser's Edge NXT configured then it can't have iMIS.
  const hasNoConflictsWithImis = account.conflictingIntegrations.includes(
    EIntegrationTypes.BlackbaudRaisersEdge,
  );

  if (!activePackage && !user?.isSudoer) {
    return (
      <SecondaryNav label={'Settings navigation'}>
        <NavItem icon={faFileInvoiceDollar} to={localUrl('/settings/billing')}>
          {t('Billing')}
        </NavItem>
        {onBillingSetting && (
          <TertiaryNav label={'Billing settings navigation'}>
            <NavItem to={localUrl('/settings/billing/configurations')}>
              {t('Configurations')}
            </NavItem>
            <NavItem to={localUrl('/settings/billing/license')}>{t('License')}</NavItem>
            <NavItem to={localUrl('/settings/billing/invoices')}>{t('Invoices')}</NavItem>
          </TertiaryNav>
        )}
      </SecondaryNav>
    );
  }

  return (
    <SecondaryNav label={'Settings navigation'}>
      <NavItem icon={faUser} to={localUrl('/settings/user')}>
        {t('User')}
      </NavItem>
      {onUserSetting && (
        <TertiaryNav label={'User settings navigation'}>
          <NavItem to={localUrl('/settings/user/notifications')}>{t('Notifications')}</NavItem>
          <NavItem to={localUrl('/settings/user/profile')}>{t('Profile')}</NavItem>
          <NavItem to={localUrl('/settings/user/reset-password')}>{t('Reset Password')}</NavItem>
        </TertiaryNav>
      )}
      <NavItem icon={faBuilding} to={localUrl('/settings/account')}>
        {t('Account')}
      </NavItem>
      {onAccountSetting && (
        <TertiaryNav label={'Account settings navigation'}>
          {(account.get('is_agency') || user.isSudoer) && (
            <NavItem to={localUrl('/settings/account/advertisers')}>{t('Advertisers')}</NavItem>
          )}
          <NavItem to={localUrl('/settings/account/conversions')}>{t('Conversions')}</NavItem>
          <NavItem to={localUrl('/settings/account/domains')}>{t('Domains')}</NavItem>
          <NavItem to={localUrl('/settings/account/allow-list')}>{t('Domain Allow List')}</NavItem>
          <NavItem to={localUrl('/settings/account/emails')}>{t('Emails')}</NavItem>
          <NavItem to={localUrl('/settings/account/ip-filtering')}>{t('IP Filtering')}</NavItem>
          <NavItem to={localUrl('/settings/account/fonts')}>{t('Fonts')}</NavItem>
          {isFeathrAdmin && (
            <NavItem to={localUrl('/settings/account/profile')}>{t('Profile')}</NavItem>
          )}
          <NavItem to={localUrl('/settings/account/subscriptions')}>{t('Subscriptions')}</NavItem>
          {isAdmin && <NavItem to={localUrl('/settings/account/users')}>{t('Users')}</NavItem>}
        </TertiaryNav>
      )}
      <NavItem icon={faLink} to={localUrl('/settings/integrations')}>
        {t('Integrations')}
      </NavItem>
      {onIntegrationSetting && (
        <TertiaryNav label={'Integration settings navigation'}>
          {showAPIKeys && (
            <NavItem to={localUrl('settings/integrations/api-keys')}>{t('API Keys')}</NavItem>
          )}
          {showGoogleAds && (
            <NavItem to={localUrl('/settings/integrations/google-ads')}>{t('Google Ads')}</NavItem>
          )}
          {showImis && hasNoConflictsWithImis && (
            <NavItem to={localUrl('/settings/integrations/imis')}>{t('iMIS')}</NavItem>
          )}
          <NavItem to={localUrl('/settings/integrations/meta')}>{t('Meta')}</NavItem>
          {showBlackbaudRaisersEdge && hasNoConflictsWithBlackbaud && (
            <NavItem to={localUrl('/settings/integrations/raisers-edge')}>
              {t("Raiser's Edge NXT")}
            </NavItem>
          )}
        </TertiaryNav>
      )}
      <NavItem icon={faFileInvoiceDollar} to={localUrl('/settings/billing')}>
        {t('Billing')}
      </NavItem>
      {onBillingSetting && (
        <TertiaryNav label={'Billing settings navigation'}>
          <NavItem to={localUrl('/settings/billing/configurations')}>{t('Configurations')}</NavItem>
          <NavItem to={localUrl('/settings/billing/license')}>{t('License')}</NavItem>
          <NavItem to={localUrl('/settings/billing/invoices')}>{t('Invoices')}</NavItem>
        </TertiaryNav>
      )}
      {isPro && (
        <NavItem icon={faRectanglePro} to={localUrl('/settings/pro-resources')}>
          {t('Resources')}
        </NavItem>
      )}
    </SecondaryNav>
  );
}

export default observer(SettingsNav);
